import { DragDropContext, type DropResult, type OnDragEndResponder } from '@hello-pangea/dnd';
import { ErrorBoundary } from '@src/components/ErrorBoundary';
import { Loader, LoaderWrapper } from '@src/components/Loader';
import type { MarketDataCardConfig } from '@talos/kyoko';
import {
  EmptyMessage,
  EmptyView,
  Icon,
  IconName,
  MarketDataCardPlaceholderWrapper,
  Tour,
  usePageVisibility,
  useSecuritiesContext,
} from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { usePanelContext } from '../../../../components/Layout/providers/PanelContextProvider';
import { TourName } from '../../../../tokens/tours';
import { DroppableContainer } from './DroppableContainer';
import { useCardColumns } from './hooks/useCardColumns';
import { AddButton, Cards, Column, MarketDataCardsWrapper } from './styles';

export interface MarketDataCardsProps {
  columnsTabID: string | undefined;
  columns: MarketDataCardConfig[][];
  createCard: (columnIndex: number) => void;
  moveCard: (e: DropResult<string>) => void;
  cardFilter: string;
  isAddingCard: boolean;
  sortCards: boolean;
  isShowingPlaceholders: boolean;
  selectedTabID: string;
  handleAddCard: () => void;
  deleteCard: (card: MarketDataCardConfig) => void;
  saveCard: (card: MarketDataCardConfig) => void;
  hidePlaceholders: () => void;
  defaultSubAccountId: number | undefined;
}

export function MarketDataCards({
  columnsTabID,
  columns,
  createCard,
  moveCard,
  deleteCard,
  saveCard,
  hidePlaceholders,
  cardFilter,
  sortCards,
  isAddingCard,
  isShowingPlaceholders,
  selectedTabID,
  handleAddCard,
  defaultSubAccountId,
}: MarketDataCardsProps) {
  const theme = useTheme();
  const isVisible = usePageVisibility();
  const { isPopout } = usePanelContext();
  const [renderCards, setRenderCards] = useState(true);
  const { securitiesBySymbol } = useSecuritiesContext();
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [showEmptyColumns, setShowEmptyColumns] = useState(false);

  // Since most browsers reduce the resources given to background tabs, we keep track
  // of the page visibility and when it's not visible, start a timer that once triggered,
  // prevents the rendering of all the cards. When the page becomes visible again the
  // cards are automatically rerendered. When removed all their subscriptions are
  // cancelled also reducing the server load.
  useEffect(() => {
    if (isVisible || isPopout) {
      setRenderCards(true);
      if (hideTimer.current != null) {
        clearTimeout(hideTimer.current);
        hideTimer.current = null;
      }
    } else {
      if (hideTimer.current == null && !isPopout) {
        hideTimer.current = setTimeout(() => {
          setRenderCards(false);
        }, 5000);
      }
    }
  }, [isVisible, isPopout]);

  const lastColumnIndex = columns?.reduceRight((res, el, index) => (el.length > 0 ? Math.max(res, index) : res), -1);

  const columnsToRender = useCardColumns(sortCards, isAddingCard, columns, securitiesBySymbol);

  const handleInitDrag = useCallback(() => setShowEmptyColumns(true), []);

  const handleEndDrag = useCallback<OnDragEndResponder<string>>(
    e => {
      setShowEmptyColumns(false);
      moveCard(e);
    },
    [moveCard]
  );

  if (columns == null) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }

  const hasNoColumns = columns.every(c => c.length === 0);

  return (
    <MarketDataCardsWrapper>
      <ErrorBoundary>
        <Cards isEmptyView={hasNoColumns && !isAddingCard}>
          {hasNoColumns && !isAddingCard
            ? // Only render empty view if the ID of columns are the same as the current market id.
              selectedTabID === columnsTabID && (
                <EmptyView>
                  <AddButton onClick={handleAddCard}>
                    <EmptyMessage>No cards have been set up yet</EmptyMessage>
                    <Icon icon={IconName.Plus} size={48} style={{ marginBottom: theme.spacing16 }} />
                    Add your first card
                  </AddButton>
                </EmptyView>
              )
            : renderCards && (
                <DragDropContext onBeforeCapture={handleInitDrag} onDragEnd={handleEndDrag}>
                  <Tour tour={TourName.v70MarketCardUpdate} />
                  {columnsToRender.map((cards, index) => (
                    <Column key={index}>
                      {isShowingPlaceholders && (
                        <MarketDataCardPlaceholderWrapper
                          onClick={() => createCard(index)}
                          data-testid="market-data-card-placeholder"
                        >
                          <Icon icon={IconName.Plus} size={48} />
                        </MarketDataCardPlaceholderWrapper>
                      )}
                      <DroppableContainer
                        key={index}
                        disableDrag={sortCards}
                        columnIndex={index}
                        lastColumnIndex={showEmptyColumns ? columns.length : lastColumnIndex}
                        cards={cards}
                        cardFilter={cardFilter}
                        deleteCard={deleteCard}
                        saveCard={saveCard}
                        hidePlaceholders={hidePlaceholders}
                        defaultSubAccountId={defaultSubAccountId}
                      />
                    </Column>
                  ))}
                </DragDropContext>
              )}
        </Cards>
      </ErrorBoundary>
    </MarketDataCardsWrapper>
  );
}
